import { inject, Injectable } from "@angular/core";
import { GraphService } from "@app/kernel/graphql/services/graph.service";
import { QUOTATION_NOTIFICATION_SETTINGS_QUERY } from "../query/quotation-notification-settings.query";
import { IQuotationNotificationSettings } from "../interfaces/quotation-notification-settings.interface";
import { map, Observable } from "rxjs";

/**
 * API service for loading quotation notification settings through GraphQL.
 *
 * @remarks
 * This service is consumed by the quotation notification settings resolver to
 * preload page data before component rendering.
 */
@Injectable({ providedIn: "root" })
export class QuotationNotificationSettingsApiService {
  /** GraphQL gateway used to construct watched queries. */
  private graphService = inject(GraphService);

  /**
   * Fetches quotation notification settings used by the quotation settings form.
   *
   * @remarks
   * The response includes:
   * - `issueQuotation` settings (with `enableOwner`)
   * - `requestQuotation` settings
   *
   * @returns Observable emitting the current {@link IQuotationNotificationSettings}.
   * @throws Propagates the first GraphQL error when the server returns an error list.
   */
  getQuotationNotificationSettings(): Observable<IQuotationNotificationSettings> {
    return this.graphService
      .constructWatchQuery<{
        getQuotationNotificationSettings: IQuotationNotificationSettings;
      }>([QUOTATION_NOTIFICATION_SETTINGS_QUERY], "getQuotationNotificationSettings")
      .pipe(
        map(res => {
          if (res.errors?.length) throw res.errors[0];
          return res.data.getQuotationNotificationSettings;
        })
      );
  }
}
